import React from "react";
import Reveal from "./Reveal";
import { useT } from "@/hooks/useT";

/* ─── Two pools of brand chips ─── */
const BRANDS_A = [
  { name: "Booking.com",   bg: "#003580", mark: <span className="font-display text-[15px] font-extrabold text-white">B<span className="text-[#FEBA02]">.</span></span> },
  { name: "Airbnb",        bg: "#FF5A5F", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5 fill-white" aria-hidden>
        <path d="M16 2c-3 0-5 2.2-6.8 5.4C5.1 14.7 2 21.7 2 25c0 3 2.2 5 5 5 2.4 0 4.7-1.6 9-7 4.3 5.4 6.6 7 9 7 2.8 0 5-2 5-5 0-3.3-3.1-10.3-7.2-17.6C20 4.2 18 2 16 2z" />
      </svg>
    ) },
  { name: "Expedia",       bg: "#FFC72C", mark: <span className="font-display text-[12px] font-extrabold text-[#191E3B]">EX</span> },
  { name: "Agoda",         bg: "#5392F9", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
        <circle cx="14" cy="14" r="9" fill="#fff" />
        <circle cx="20" cy="20" r="4" fill="#FF6F00" />
      </svg>
    ) },
  { name: "Google Hotels", bg: "#FFFFFF", border: "#E2E8F0", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
        <path fill="#4285F4" d="M16 14v4.4h6.2c-.3 1.4-1 2.6-2.1 3.4l3.5 2.7c2-1.9 3.2-4.6 3.2-7.8 0-.7-.1-1.5-.2-2.2H16z" />
        <path fill="#34A853" d="M9 18.7l-3.6 2.9C7.2 25.3 11.3 28 16 28c2.7 0 4.9-.9 6.6-2.4l-3.5-2.7c-1 .6-2.2 1-3.1 1-2.4 0-4.5-1.6-5.2-3.8z" />
        <path fill="#FBBC05" d="M5.4 10.4C4.5 12.1 4 14 4 16s.5 3.9 1.4 5.6L9 18.7c-.2-.6-.4-1.3-.4-2s.2-1.4.4-2L5.4 10.4z" />
        <path fill="#EA4335" d="M16 8.9c1.5 0 2.8.5 3.9 1.5l2.9-2.9C20.9 5.7 18.7 4.8 16 4.8c-4.7 0-8.8 2.7-10.6 6.6L9 14.3c.7-2.2 2.8-3.8 5.2-3.8z" />
      </svg>
    ) },
  { name: "Vrbo",          bg: "#245ABF", mark: <span className="font-display text-[11px] font-extrabold text-white">vrbo</span> },
  { name: "Hotelbeds",     bg: "#0E2A47", mark: <span className="font-display text-[11px] font-extrabold text-[#FFB400]">hb</span> },
  { name: "TripAdvisor",   bg: "#34E0A1", mark: <span className="font-display text-[14px] font-extrabold text-[#0F172A]">◎</span> },
];

const BRANDS_B = [
  { name: "Trivago",       bg: "#E10E49", mark: <span className="font-display text-[11px] font-extrabold text-white">trv</span> },
  { name: "Despegar",      bg: "#FF7F00", mark: <span className="font-display text-[14px] font-extrabold text-white">D</span> },
  { name: "HotelTonight",  bg: "#EF4136", mark: <span className="font-display text-[11px] font-extrabold text-white">HT</span> },
  { name: "Hostelworld",   bg: "#F39200", mark: <span className="font-display text-[14px] font-extrabold text-white">H</span> },
  { name: "Kayak",         bg: "#FF690F", mark: <span className="font-display text-[11px] font-extrabold text-white">kyk</span> },
  { name: "Hotels.com",    bg: "#D32F2F", mark: <span className="font-display text-[11px] font-extrabold text-white">h.</span> },
  { name: "Traveloka",     bg: "#0194F3", mark: <span className="font-display text-[11px] font-extrabold text-white">tlk</span> },
  { name: "MakeMyTrip",    bg: "#E73C33", mark: <span className="font-display text-[11px] font-extrabold text-[#FCB813]">MMT</span> },
];

function Chip({ brand }) {
  return (
    <div className="flex shrink-0 items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 transition-all hover:border-[#CBD5E1] hover:shadow-[0_8px_22px_-12px_rgba(15,23,42,0.25)]">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1 ring-black/[0.06]"
        style={{ background: brand.bg, border: brand.border ? `1px solid ${brand.border}` : undefined }}
      >
        {brand.mark}
      </span>
      <span className="whitespace-nowrap text-[13px] font-semibold text-[#0F172A]">{brand.name}</span>
    </div>
  );
}

/**
 * Channel manager promo strip — copy left (inside container) + logos right (extended to viewport edge).
 */
export default function ChannelMarquee() {
  const t = useT();
  return (
    <section
      className="relative overflow-hidden bg-white"
      aria-labelledby="channel-marquee-heading"
      data-testid="channel-marquee"
    >
      <div className="relative py-16 md:py-20">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-12">
          {/* ─── LEFT — Copy (aligned with container-akm) ─── */}
          <div
            className="shrink-0 pr-5 md:pr-8 lg:pr-0"
            style={{
              paddingLeft: "max(1.25rem, calc((100vw - 1350px) / 2 + 2rem))",
            }}
          >
            <Reveal variant="left" className="lg:w-[420px]" data-testid="channel-marquee-copy">
              <span className="inline-flex items-center gap-2 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.18em] text-[#EA580C]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
                Channel Manager
              </span>
              <h2
                id="channel-marquee-heading"
                className="mt-3 font-display font-extrabold tracking-[-0.025em] text-[#0B1220]"
                style={{
                  fontSize: "clamp(1.35rem, 0.8rem + 1.1vw, 2rem)",
                  lineHeight: 1.18,
                }}
              >
                {t("Sinkronizim automatik", "Automatic sync")} <br className="hidden sm:block" />
                {t("me ", "with ")}<span className="text-[#F26322]">{t("platformat kryesore", "the major platforms")}</span>
              </h2>
              <p className="mt-3 max-w-md text-[14px] leading-[1.6] text-[#475569]">
                {t(
                  "Disponueshmëria, çmimet dhe rezervimet përditësohen automatikisht në çdo kanal. Pa rezervime të dyfishta, pa stres dhe pa kohë të humbur.",
                  "Availability, rates and reservations update automatically across every channel. No double bookings, no stress, no wasted time."
                )}
              </p>
            </Reveal>
          </div>

          {/* ─── RIGHT — Logos extending to right viewport edge ─── */}
          <Reveal variant="right" delay={150}
            className="relative min-w-0 flex-1 overflow-hidden mask-fade-x-right"
            data-testid="channel-marquee-track"
          >
            {/* Row 1 — right→left */}
            <div className="marquee-channels">
              {[...BRANDS_A, ...BRANDS_A, ...BRANDS_A].map((b, i) => (
                <Chip key={`a-${i}`} brand={b} />
              ))}
            </div>

            {/* Row 2 — right→left, slower */}
            <div className="marquee-channels-rev mt-3">
              {[...BRANDS_B, ...BRANDS_B, ...BRANDS_B].map((b, i) => (
                <Chip key={`b-${i}`} brand={b} />
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
